import React, { useState, useEffect, useRef } from "react";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Divider,
  CloseButton,
  FieldWrapper,
  Input,
  ErrorMessage,
  ButtonWrapper,
  GroupName,
} from "./DocumentModal.styles";
import { DocumentModalProps } from "./DocumentModal.types";
import Button from "../../Buttons/Button";
import { closeIcon as CloseIcon } from "assets/icons";
import DatePickerInput from "../../CalendarInput/CalendarInput";
import DropdownWithInput from "../../Dropdowns/DropdownWithInput";
import TitleTooltip from "../../TitleTooltip/TitleTooltip";

const DocumentModal: React.FC<DocumentModalProps> = ({
  title,
  fields,
  onSubmit,
  submitButtonText = "Сохранить",
  onCancel,
  initialValues = {},
  versionId,
  groupName,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});

  useEffect(() => {
    const initialFormData = fields.reduce((acc, field) => {
      acc[field.name] = initialValues[field.name] || "";
      return acc;
    }, {} as Record<string, string>);
    setFormData(initialFormData);
  }, [fields, initialValues]);

  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  // Первоначальная подстройка высоты textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    const allRequiredFieldsFilled = fields.every(
      (field) => !field.required || formData[field.name]?.trim() !== ""
    );
    setIsSubmitDisabled(!allRequiredFieldsFilled);
  }, [formData, fields]);

  const handleTextareaChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleSubmit = async () => {
    const newErrors: Record<string, string | null> = {};
    fields.forEach((field) => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = "Это поле обязательно";
      }
    });
    setErrors(newErrors);
    onSubmit(formData);
  
  };
  
  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <h2>{title}</h2>
          <CloseButton aria-label="Закрыть" onClick={onCancel}>
            <CloseIcon aria-hidden="true" />
          </CloseButton>
        </ModalHeader>

        <Divider />

        {groupName && <GroupName>{groupName}</GroupName>}

        <ModalBody>
          <form onSubmit={(e) => e.preventDefault()}>
            {fields.map((field) => (
              <FieldWrapper key={field.name}>
                {errors[field.name] && (
                  <ErrorMessage>{errors[field.name]}</ErrorMessage>
                )}
                {field.field_type === "date" ? (
                  <DatePickerInput
                    title={field.placeholder}
                    value={formData[field.name]}
                    placeholder={field.placeholder}
                    onChange={(value) => handleChange(field.name, value)}
                  />
                ) : field.field_type === "textarea" ? (
                  <TitleTooltip text={field.placeholder || ""}>
                  <Input
                    id={field.name}
                    as="textarea"
                    ref={textareaRef}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={(e) => handleTextareaChange(field.name, e.target.value)}
                    hasError={!!errors[field.name]}
                  />
                  </TitleTooltip>
                ) : field.field_type === "select" ? (
                  <DropdownWithInput
                    options={field.options || []}
                    placeholder={field.placeholder || "Выберите значение"}
                    value={formData[field.name]}
                    onChange={(value) => handleChange(field.name, value)}
                  />
                ) : (
                  <TitleTooltip text={field.placeholder || ""}>
                  <Input
                    type={field.field_type}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    hasError={!!errors[field.name]}
                  />
                  </TitleTooltip>
                )}
              </FieldWrapper>
            ))}
          </form>
        </ModalBody>

        <ModalFooter>
          <ButtonWrapper>
            <Button
              type="button"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
              fullWidth
            >
              {submitButtonText}
            </Button>
          </ButtonWrapper>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DocumentModal;
